import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, Check, X, RefreshCw, Wand2 } from "lucide-react";
import api from "../lib/api";
import { sportIcon, intensityClass, fmtDate } from "../lib/workout";
import { Skeleton } from "../components/ui/skeleton";
import { Button } from "../components/ui/button";
import PlanEditDialog from "../components/PlanEditDialog";

export default function Plan() {
  const [data, setData] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get("/plan").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const today = new Date().toISOString().slice(0, 10);

  if (!data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-40" />
        {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-20 w-full rounded-2xl" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight text-foreground" data-testid="plan-title">Your plan</h1>
          {data.plan_note && <p className="text-muted-foreground mt-2 max-w-xl">{data.plan_note}</p>}
        </div>
        <Button data-testid="plan-edit-btn" onClick={() => setEditOpen(true)} className="rounded-xl font-semibold shrink-0 active:scale-95 transition-transform">
          <Wand2 size={16} className="mr-1.5" /> Edit with Caden
        </Button>
      </div>

      <div className="space-y-3" data-testid="plan-list">
        {data.workouts.map((w, i) => {
          const Icon = sportIcon(w.sport);
          const isToday = w.date === today;
          const isPast = w.date < today;
          return (
            <motion.button
              key={w.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03 }}
              onClick={() => navigate(`/workout/${w.id}`)}
              data-testid={`plan-item-${w.id}`}
              className={`w-full text-left rounded-2xl border p-4 sm:p-5 flex items-center gap-4 transition-colors ${
                isToday ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/40"
              } ${isPast && w.status === "planned" ? "opacity-60" : ""}`}
            >
              <div className="text-center w-12 shrink-0">
                <p className="text-xs font-bold uppercase text-muted-foreground">{fmtDate(w.date).split(" ")[0]}</p>
                <p className="font-heading font-black text-xl text-foreground">{new Date(w.date + "T00:00:00").getDate()}</p>
              </div>

              <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${w.is_rest ? "bg-muted" : "brand-gradient"}`}>
                <Icon className={w.is_rest ? "text-muted-foreground" : "text-white"} size={20} />
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-heading font-bold text-foreground truncate">{w.title}</p>
                  {w.adapted && <RefreshCw size={13} className="text-brand-orange shrink-0" data-testid={`plan-adapted-${w.id}`} />}
                </div>
                <p className="text-sm text-muted-foreground truncate">{w.purpose || w.type}</p>
              </div>

              <div className="flex flex-col items-end gap-1.5 shrink-0">
                {w.status === "completed" && <span className="text-emerald-500" data-testid="plan-status-completed"><Check size={18} /></span>}
                {w.status === "skipped" && <span className="text-muted-foreground"><X size={18} /></span>}
                {!w.is_rest && (
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${intensityClass(w.intensity)}`}>
                    {w.intensity}
                  </span>
                )}
                {!w.is_rest && (
                  <span className="flex items-center gap-1 text-xs text-muted-foreground"><Clock size={11} />{w.duration_min}m</span>
                )}
              </div>
            </motion.button>
          );
        })}
      </div>

      <PlanEditDialog open={editOpen} onOpenChange={setEditOpen} onApplied={load} />
    </div>
  );
}
